import classnames from 'classnames';
import { head, last, map } from 'lodash';
import moment from 'moment';
import uuid from 'node-uuid';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import styled, { cx } from 'react-emotion';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { Button } from 'web-ui-components/lib/atoms/buttons';
import { Label } from 'web-ui-components/lib/atoms/forms';
import { Icon } from 'web-ui-components/lib/atoms/iconography';
import { Popover } from 'web-ui-components/lib/atoms/popovers';
import { Column, Container, FlexColumn, FlexRow, Row, ServicingSection } from 'web-ui-components/lib/global/layout';
import { ValidationMessage } from 'web-ui-components/lib/molecules/forms';
import { BackButton } from 'web-ui-components/lib/molecules/navigation';

import withContent from '../../../containers/withContent';
import withMissingDetails from '../../../containers/withMissingDetails';
import { withScrollTop } from '../../../containers/withScrollTop';

import AddMoreButton from '../../../sharedComponents/AddMoreButton/';
import { DestinationAccount } from '../../../sharedComponents/FromAccount/FromAccount';
import PageNavigation from '../../../sharedComponents/PageNavigation/PageNavigation';
import { payeePropTypes } from '../../../sharedComponents/PayeeList/propTypes';
import SiteHeader from '../../../sharedComponents/SiteHeader/SiteHeader';

import { BACS_DEBIT_CODE, TYPE_BACS_DEBIT, bacsNameMaxLength, dateFormat } from '../../../config';
import { fromAccountPropType, toAccountPropType } from '../../../states/accounts/propTypes';
import {
	getAccountsForBacsOnType,
	getBacsIdsByAccountIdAndBacsType,
	getInternalAccountsForBacs,
} from '../../../states/accounts/selectors';
import { hasApiCallInProgress } from '../../../states/api/selectors';
import {
	addMultiplePayments,
	copyRefToAll,
	copyTransCodeToAll,
	removePayment,
	updateBacsId,
	updateFromAccount,
	updatePaymentName,
	updateReference,
	updateSubmissionDate,
	updateToAccount,
} from '../../../states/bacsDebit/actions';
import {
	getBacsDebitPaymentId,
	getBacsId,
	getBacsLimit,
	getBacsPaymentIds,
	getBacsPaymentName,
	getBacsTotal,
	getFirstPaymentHasFromAccount,
	getPayments,
	getPaymentsToInternalCreditCards,
	getSettlementDate,
	getSubmissionDate,
	getToAccount,
	getTotalLimitExceeded,
	hasMultiplePayments,
	isNumberOfPaymentsLimit,
} from '../../../states/bacsDebit/selectors';
import { getPayees } from '../../../states/payees/selectors';
import {
	hideErrorMsg,
	initValidationForTemplate,
	showAllErrors,
	updateValidation,
} from '../../../states/validation/actions';
import { formValid, getValidationRowState } from '../../../states/validation/selectors';
import pageContent, { contentPropTypes } from '../DebitLandingPage/Debit.content';
import styles from './BacsPaymentStyles';
import BacsId from './Form/BacsId';
import PaymentName from './Form/PaymentName';
import SubmissionDate from './Form/SubmissionDate';
import DebitPaymentRow from './PaymentsRow/DebitPaymentRow';

import withAccountSelection from '../../../containers/withAccountSelection';
import { getReference } from '../../../helpers/fieldHelpers';
import DeleteTemplate from '../../../sharedComponents/DeleteTemplate/DeleteTemplate';
import Notifications from '../../../sharedComponents/Notifications';
import SaveAsTemplateToggle from '../../../sharedComponents/SaveAsTemplateToggle/SaveAsTemplateToggle';
import TemplateButtons from '../../../sharedComponents/TemplateButtons/TemplateButtons';
import TemplateName from '../../../sharedComponents/TemplateName/TemplateName';
import UnSavedWarningModal from '../../../sharedComponents/UnSavedWarningModal/UnSavedWarningModal';
import { setCanAddMultiplePayees } from '../../../states/modes/actions';
import { isAuthorisationEdit } from '../../../states/modes/selectors';
import { goToPage, pages } from '../../../states/page/actions';
import {
	clearSelectedPayment,
	setSelectedFromAccount,
	setSelectedPaymentId,
} from '../../../states/payeeSelector/actions';
import {
	SUBMIT_CREATE_TEMPLATE_SUCCESS,
	addTemplateNameValidation,
	overWriteTemplate,
	removeTemplateNameValidation,
	savePaymentsAsTemplateAndExit,
	savePaymentsAsTemplateAndMakePayment,
	savePaymentsAsTemplateAndNotify,
	submitDeleteTemplate,
} from '../../../states/templates/actions';
import {
	getCurrentTemplateId,
	getIsVersionConflict,
	getTemplateLastUpdatedBy,
	getTemplateLastUpdatedTime,
	getTemplateName,
} from '../../../states/templates/selectors';
import shallowEqual from '../../../utils/compare';

const mapState = (state) => ({
	destinationAccounts: getAccountsForBacsOnType(state, BACS_DEBIT_CODE),
	sourcePayees: getPayees(state),
	bacsIds: getBacsIdsByAccountIdAndBacsType(state, getToAccount(state), BACS_DEBIT_CODE),
	bacsLimit: getBacsLimit(state),
	hasMultiplePayments: hasMultiplePayments(state),
	paymentIds: getBacsPaymentIds(state),
	submissionDate: getSubmissionDate(state),
	settlementDate: getSettlementDate(state),
	hasReachedNumberOfPaymentsLimit: isNumberOfPaymentsLimit(state),
	total: getBacsTotal(state),
	hasApiCallInProgress: hasApiCallInProgress(state),
	isFormValid: formValid(state),
	paymentName: getBacsPaymentName(state),
	validations: getValidationRowState(state, 'from'),
	payments: getPayments(state),
	destinationAccount: getToAccount(state),
	bacsId: getBacsId(state),
	totalLimitExceeded: getTotalLimitExceeded(state),
	sourceAccounts: getInternalAccountsForBacs(state, getToAccount(state)),
	paymentsToInternalCreditCards: getPaymentsToInternalCreditCards(state),
	isAuthorisationEdit: isAuthorisationEdit(state),
	templateId: getCurrentTemplateId(state),
	isVersionConflict: getIsVersionConflict(state),
	templateLastUpdatedBy: getTemplateLastUpdatedBy(state),
	templateLastUpdatedTime: getTemplateLastUpdatedTime(state),
	templateName: getTemplateName(state),
	firstPaymentHasFromAccount: getFirstPaymentHasFromAccount(state),
	bacsDebitPaymentId: getBacsDebitPaymentId(state),
});

const mapDispatch = (dispatch) => ({
	handleSelectDestinationAccount: (accountId) => dispatch(updateToAccount({ accountId })),
	handleBacsIdChange: (bacsId) => dispatch(updateBacsId({ bacsId })),
	handlePaymentNameChange: (paymentName) => dispatch(updatePaymentName({ paymentName })),
	addMultiplePayments: (payments) => dispatch(addMultiplePayments(payments)),
	handleSelectSourceAccount: (id, accountId, sourcePayees) => {
		dispatch(updateFromAccount({ id, accountId }));
		dispatch(updateReference({ id, reference: getReference(sourcePayees, accountId) }));
	},
	showAllErrors: () => dispatch(showAllErrors()),
	updateValidation: (key, isValid) =>
		dispatch(
			updateValidation({
				id: 'from',
				key,
				isValid,
			})
		),
	hideErrorMsg: (key) =>
		dispatch(
			hideErrorMsg({
				id: 'from',
				key,
			})
		),
	handleDateChange: (submissionDate) => {
		dispatch(updateSubmissionDate({ submissionDate }));
	},
	copyReferenceToAll: (paymentsToInternalCreditCards) => dispatch(copyRefToAll(paymentsToInternalCreditCards)),
	copyTransactionCodeToAll: () => dispatch(copyTransCodeToAll()),
	handleCancelSourceSelect: (id) =>
		dispatch(
			removePayment({
				id,
			})
		),
	handleSaveTemplate: () => dispatch(savePaymentsAsTemplateAndNotify(TYPE_BACS_DEBIT)),
	handleSaveTemplateAndExit: () => dispatch(savePaymentsAsTemplateAndExit(TYPE_BACS_DEBIT)),
	handleSaveTemplateAndMakePayment: () => dispatch(savePaymentsAsTemplateAndMakePayment(TYPE_BACS_DEBIT)),
	initValidationForTemplate: () => dispatch(initValidationForTemplate()),
	handleDeleteTemplate: () => dispatch(submitDeleteTemplate()),
	removeTemplateNameValidation: () => dispatch(removeTemplateNameValidation()),
	addTemplateNameValidation: () => dispatch(addTemplateNameValidation()),
	handleOverwriteTemplate: () => dispatch(overWriteTemplate(TYPE_BACS_DEBIT)),
	goToPayeeSelector: () => dispatch(goToPage(pages.BacsDebitPayeeSelector)),
	selectPayment: (paymentId) => dispatch(setSelectedPaymentId(paymentId)),
	setSelectedDestinationAccount: (account) => dispatch(setSelectedFromAccount(account)),
	clearSelectedPayment: () => dispatch(clearSelectedPayment()),
	setCanAddMultiplePayees: (canAddMultiplePayees) => dispatch(setCanAddMultiplePayees(canAddMultiplePayees)),
});

const StyledHeaderRow = styled('div')`
	display: flex;
	justify-content: space-between;
`;
class DebitPaymentsPage extends Component {
	constructor(props) {
		super(props);

		this.state = {
			showDestinationAccountSelector: false,
			popovers: {},
			showSourcePayeeList: false,
			selectedSourceAccount: '',
			showValidation: true,
			showErrors: false,
			saveAsTemplate: false,
			showSameRefPopover: false,
		};
		this.handleAddMore = this.handleAddMore.bind(this);
		this.handleDestinationCardClick = this.handleDestinationCardClick.bind(this);
		this.togglePopover = this.togglePopover.bind(this);
		this.handleSourceCardClick = this.handleSourceCardClick.bind(this);
		this.onPayeeListContinue = this.onPayeeListContinue.bind(this);
		this.handleContinue = this.handleContinue.bind(this);
		this.handleCopyRefToAll = this.handleCopyRefToAll.bind(this);
		this.handleSameRefClick = this.handleSameRefClick.bind(this);
		this.handleCancelSourceSelect = this.handleCancelSourceSelect.bind(this);
		this.handleBack = this.handleBack.bind(this);
		this.handleSaveAsTemplateToggle = this.handleSaveAsTemplateToggle.bind(this);
	}

	componentWillMount() {
		const { templateMode, initValidationForTemplate } = this.props;
		if (templateMode) {
			initValidationForTemplate();
		}
	}

	componentDidMount() {
		const { bacsIds, handleBacsIdChange, handleDisplayFullwidth, clearSelectedPayment } = this.props;
		handleDisplayFullwidth();
		clearSelectedPayment();
		if (bacsIds.length === 1) {
			handleBacsIdChange(head(bacsIds));
		}
	}

	shouldComponentUpdate(nextProps, nextState) {
		const { payments, ...propsWithoutPayments } = this.props;
		const { payments: nextPayments, ...nextPropsWithoutPayments } = nextProps;
		return !shallowEqual(propsWithoutPayments, nextPropsWithoutPayments) || !shallowEqual(this.state, nextState);
	}

	componentDidUpdate(prevProps) {
		const { destinationAccount, bacsIds, handleBacsIdChange, templateMode } = this.props;
		const paymentFromTemplate = prevProps.templateMode && !templateMode;
		const destinationAccountChanged = destinationAccount !== prevProps.destinationAccount;
		if ((destinationAccountChanged || paymentFromTemplate) && bacsIds.length === 1) {
			handleBacsIdChange(head(bacsIds));
		}
	}

	onPayeeListContinue(selectedSourcePayees) {
		const { addMultiplePayments, sourcePayees, handleSelectSourceAccount } = this.props;

		if (this.state.addMorePayments) {
			const multiplePayments = map(selectedSourcePayees, (payee) => ({
				id: uuid.v1(),
				fromAccount: payee,
				reference: getReference(sourcePayees, payee),
			}));

			addMultiplePayments(multiplePayments);
		} else {
			const { selectedPaymentId } = this.state;
			handleSelectSourceAccount(selectedPaymentId, head(selectedSourcePayees), sourcePayees);
		}

		this.setState({
			showSourcePayeeList: false,
			selectedSourceAccount: undefined,
			selectedPaymentId: undefined,
		});
	}

	handleAddMore() {
		const { setSelectedDestinationAccount, destinationAccount, setCanAddMultiplePayees } = this.props;
		setCanAddMultiplePayees(true);
		setSelectedDestinationAccount(destinationAccount);
		this.props.goToPayeeSelector();
	}

	togglePopover(key) {
		const { popovers } = this.state;

		this.setState({
			popovers: {
				[key]: !popovers[key],
			},
		});
	}
	handleDestinationCardClick() {
		this.setState({
			showDestinationAccountSelector: !this.state.showDestinationAccountSelector,
		});
	}
	handleSourceCardClick(selectedPaymentId) {
		const {
			selectPayment,
			goToPayeeSelector,
			setSelectedDestinationAccount,
			destinationAccount,
			setCanAddMultiplePayees,
		} = this.props;
		setCanAddMultiplePayees(false);
		selectPayment(selectedPaymentId);
		setSelectedDestinationAccount(destinationAccount);
		goToPayeeSelector();
	}

	handleCancelSourceSelect() {
		const { paymentIds, handleCancelSourceSelect } = this.props;
		if (!this.state.selectedSourceAccount && paymentIds.length > 1) handleCancelSourceSelect(last(paymentIds));

		this.setState({
			showSourcePayeeList: false,
		});
	}

	handleContinue() {
		const { isFormValid, showAllErrors, handleSaveTemplate, handleContinue } = this.props;
		showAllErrors();

		if (isFormValid) {
			if (this.state.saveAsTemplate) {
				return handleSaveTemplate().then((response) => {
					if (response.type === SUBMIT_CREATE_TEMPLATE_SUCCESS) handleContinue();
				});
			}
			return handleContinue();
		}
		return this.setState({ showErrors: true });
	}

	handleSameRefClick(event) {
		event.preventDefault();
		this.setState({ showSameRefPopover: false });
	}

	handleCopyRefToAll() {
		const { paymentsToInternalCreditCards, copyReferenceToAll } = this.props;

		if (paymentsToInternalCreditCards.length) {
			this.setState({ showSameRefPopover: true });
		}
		copyReferenceToAll(paymentsToInternalCreditCards);
	}

	SaveTemplate(saveTemplateHandler) {
		const { isFormValid, showAllErrors } = this.props;
		showAllErrors();
		if (!isFormValid) return this.setState({ showErrors: true });

		return saveTemplateHandler();
	}

	handleBack() {
		const { handleBack, templateMode } = this.props;
		const { unfinishedTemplateModalOpen } = this.state;

		if (unfinishedTemplateModalOpen) {
			this.setState({ unfinishedTemplateModalOpen: false }, handleBack);
			return;
		}

		if (templateMode) {
			this.setState({ unfinishedTemplateModalOpen: true });
			return;
		}

		handleBack();
	}

	handleSaveAsTemplateToggle() {
		const { removeTemplateNameValidation, addTemplateNameValidation } = this.props;
		if (!this.state.saveAsTemplate) {
			this.setState({ saveAsTemplate: true });
			addTemplateNameValidation();
		} else {
			this.setState({ saveAsTemplate: false });
			removeTemplateNameValidation();
		}
	}

	render() {
		const {
			handleBacsIdChange,
			handlePaymentNameChange,
			destinationAccounts,
			content,
			bacsIds,
			paymentIds,
			hasMultiplePayments,
			sourcePayees,
			hasReachedNumberOfPaymentsLimit,
			total,
			hasApiCallInProgress,
			paymentName,
			validations,
			updateValidation,
			hideErrorMsg,
			submissionDate,
			settlementDate,
			handleDateChange,
			destinationAccount,
			bacsId,
			totalLimitExceeded,
			bacsLimit,
			sourceAccounts,
			isAuthorisationEdit,
			copyTransactionCodeToAll,
			templateMode,
			handleSaveTemplate,
			handleSaveTemplateAndExit,
			handleSaveTemplateAndMakePayment,
			templateId,
			handleDeleteTemplate,
			isVersionConflict,
			templateLastUpdatedBy,
			templateLastUpdatedTime,
			handleBack: cancelOverwrite,
			firstPaymentHasFromAccount,
			templateName,
			handleOverwriteTemplate,
			handleFromCardClick,
			showFromAccountSelector: showDestinationAccountSelector,
			bacsDebitPaymentId,
			handleSelectFromAccount,
		} = this.props;

		const {
			title,
			fullDescription,
			submissionDatePopperBody,
			settlementDatePopperBody,
			bacsIdPopperBody,
			paymentNamePopperBody,
			submissionDateLabel,
			settlementDateLabel,
			bacsIdLabel,
			paymentNameLabel,
			toLabel,
			amountLabel,
			referenceLabel,
			transactionCodeLabel,
			submissionDateDescription,
			settlementDateDescription,
			bacsIdDescription,
			paymentNameDescription,
			paymentNameRequirements,
			fromLabel,
			transactionCodePopperBody,
			templateTitle,
			templateDescription,
			saveTemplateButtonText,
			saveTemplateAndExitButtonText,
			saveTemplateAndMakePaymentButtonText,
			referenceLabelTemplate,
			amountLabelTemplate,
			transactionCodeLabelTemplate,
			templateEditTitle,
			templateMakePaymentDescription,
		} = content;

		const {
			popovers,
			showSourcePayeeList,
			showValidation,
			showErrors,
			showSameRefPopover,
			unfinishedTemplateModalOpen,
		} = this.state;

		const wrapperClass = classnames({
			'c-o-payee-list--from-state': showDestinationAccountSelector,
		});

		const renderPopper = (id, content, isOpen) => (
			<Popover
				id={`popover_${id}`}
				className="u-margin-left-1"
				isOpen={isOpen || false}
				onClick={() => this.togglePopover(id)}
				placement="top"
				content={content}
			>
				<Icon iconType="InfoOutlineLarge" size="xs" />
			</Popover>
		);

		const showBackButton = isAuthorisationEdit || templateMode;

		const getPageTitle = () => {
			if (templateMode && templateId) return templateEditTitle;
			if (templateMode) return templateTitle;
			if (isAuthorisationEdit) return `Edit ${paymentName}`;
			return title;
		};

		const getPageDescription = () => {
			if (templateMode) return templateDescription;
			if (templateId) return templateMakePaymentDescription;
			return fullDescription;
		};

		const pageTitle = getPageTitle();
		const pageDescription = getPageDescription();
		const showOverwriteModal = templateMode && templateId && isVersionConflict;
		const showTemplateButtons = destinationAccount && firstPaymentHasFromAccount;
		const showAddMore = !hasReachedNumberOfPaymentsLimit && destinationAccount && firstPaymentHasFromAccount;
		const isCreatedFromTemplate = !templateMode && !!templateId;
		const showSaveAsTemplate = !showSourcePayeeList && !templateMode && !templateId;

		return (
			<div className={wrapperClass}>
				{showBackButton && <BackButton onClick={this.handleBack} disabled={hasApiCallInProgress} />}

				{templateMode && (
					<UnSavedWarningModal
						handleClose={() => {
							this.setState({ unfinishedTemplateModalOpen: false });
						}}
						isModalOpen={unfinishedTemplateModalOpen}
						handleBack={this.handleBack}
						contentKey={templateId ? 'editTemplateContent' : 'newTemplateContent'}
					/>
				)}
				{showOverwriteModal && (
					<UnSavedWarningModal
						handleClose={cancelOverwrite}
						isModalOpen
						handleBack={handleOverwriteTemplate}
						contentKey="overwriteTemplateContent"
						user={templateLastUpdatedBy}
						dateTime={templateLastUpdatedTime}
					/>
				)}
				<SiteHeader title={pageTitle} description={pageDescription} />
				{!showSourcePayeeList && (
					<ServicingSection padded={false}>
						<Container className={cx(styles.container, styles.main__container)}>
							<div className="u-margin-bottom-2">
								<Notifications />
							</div>
							<StyledHeaderRow>
								{templateMode && <TemplateName />}
								{templateMode && templateId && <DeleteTemplate handleDeleteTemplate={handleDeleteTemplate} />}
								{isCreatedFromTemplate && (
									<p>
										<strong>{templateName}</strong>
									</p>
								)}
							</StyledHeaderRow>
							<Row>
								<Column xs={24}>
									<FlexRow>
										<FlexColumn key="fromAccount" proportion={1}>
											<div className={styles.field__label}>
												<Label className="u-type-size-uist" htmlFor="fromAccount">
													{toLabel}
												</Label>
											</div>
											<DestinationAccount
												id={bacsDebitPaymentId}
												fromAccount={destinationAccount}
												showAccountSelector={showDestinationAccountSelector}
												fromAccounts={destinationAccounts}
												handleSelectFromAccount={handleSelectFromAccount}
												handleCardClick={handleFromCardClick}
												showValidation={showValidation}
												validations={validations}
												updateValidation={updateValidation}
												hideErrorMsg={hideErrorMsg}
												title={toLabel}
											/>
										</FlexColumn>
										{!templateMode && (
											<FlexColumn key="submissionDate" proportion={1}>
												<div className={styles.field__label}>
													<Label className="u-type-size-uist" htmlFor="submissionDate">
														{submissionDateLabel}
													</Label>
													{renderPopper('submissionDate', submissionDatePopperBody, popovers.submissionDate)}
												</div>
												<SubmissionDate date={submissionDate} handleDateChange={handleDateChange} />
												<span className={styles.main__description}>{submissionDateDescription}</span>
											</FlexColumn>
										)}
										{!templateMode && (
											<FlexColumn key="settlementDate">
												<div className={styles.field__label}>
													<Label className="u-type-size-uist" htmlFor="settlementDate">
														{settlementDateLabel}
													</Label>
													{renderPopper('settlementDate', settlementDatePopperBody, popovers.settlementDate)}
												</div>

												<div className={styles.static__field}>
													{settlementDate ? settlementDate.format(dateFormat) : ''}
												</div>
												<span className={styles.main__description}>{settlementDateDescription}</span>
											</FlexColumn>
										)}
									</FlexRow>
									<FlexRow>
										{!templateMode && (
											<FlexColumn key="paymentName" proportion={1}>
												<div className={styles.field__label}>
													<Label className="u-type-size-uist" htmlFor="paymentName">
														{paymentNameLabel}
													</Label>
													{renderPopper('paymentName', paymentNamePopperBody, popovers.paymentName)}
												</div>
												<PaymentName
													id="paymentName"
													paymentName={paymentName}
													maxLength={bacsNameMaxLength}
													onBlur={handlePaymentNameChange}
													description={paymentNameRequirements}
												/>
												<span className={styles.main__description}>{paymentNameDescription}</span>
											</FlexColumn>
										)}
										{!templateMode && (
											<FlexColumn key="bacsId">
												<div className={styles.field__label}>
													<Label className="u-type-size-uist" htmlFor="bacsId">
														{bacsIdLabel}
													</Label>
													{renderPopper('bacsId', bacsIdPopperBody, popovers.bacsId)}
												</div>
												<BacsId
													bacsIdOptions={bacsIds}
													bacsId={bacsId}
													onChange={handleBacsIdChange}
													showValidation={showValidation}
													invalidMessage={content.bacsIdInvalid}
													validations={validations}
													updateValidation={updateValidation}
													hideErrorMsg={hideErrorMsg}
												/>
												{bacsLimit && (
													<span id="bacsIdLimit" className={styles.main__description}>
														{bacsLimit}
													</span>
												)}
												<span className={styles.main__description}>{bacsIdDescription}</span>
											</FlexColumn>
										)}
									</FlexRow>
								</Column>
							</Row>
						</Container>

						<div className={styles.payments__container}>
							<Container className={cx(styles.container)}>
								<Row>
									<table className={styles.payments__table}>
										<thead>
											<tr>
												<th>
													<Label className="table-head-label">{fromLabel}</Label>
												</th>
												<th>
													<Label className="table-head-label">{templateMode ? amountLabelTemplate : amountLabel}</Label>
												</th>
												<th>
													<Label className="table-head-label">
														{templateMode ? referenceLabelTemplate : referenceLabel}
														<br />
														{paymentIds.length > 1 && (
															<Button
																onClick={this.handleCopyRefToAll}
																className={styles.button__apply}
																id="copy-ref-to-all-button"
																size="xs"
															>
																{content.copyToAllButton}
															</Button>
														)}

														<Popover
															id="popover-sameRef"
															placement="left"
															isOpen={showSameRefPopover}
															content={content.sameRefPopoverMsg}
															onClick={this.handleSameRefClick}
															isButtonRequired={false}
														/>
													</Label>
												</th>
												<th>
													<Label className="table-head-label">
														{templateMode ? transactionCodeLabelTemplate : transactionCodeLabel}
														{renderPopper('transactionCode', transactionCodePopperBody, popovers.transactionCode)}
														{paymentIds.length > 1 && (
															<div className={styles.button__container}>
																<Button
																	onClick={copyTransactionCodeToAll}
																	className={styles.button__apply}
																	id="copy-transaction-code-to-all-button"
																	size="xs"
																>
																	{content.copyToAllButton}
																</Button>
															</div>
														)}
													</Label>
												</th>
												<th />
											</tr>
										</thead>
										<tbody>
											{paymentIds.map((paymentId, index) => {
												const getBiocatchIdentifier =
													paymentIds.length > 1 ? `payment-amount-${index}` : 'payment-amount-0';
												return (
													<DebitPaymentRow
														key={paymentId}
														id={paymentId}
														sourceAccounts={sourceAccounts}
														sourcePayees={sourcePayees}
														showRemoveButton={hasMultiplePayments}
														content={content}
														showValidation={showValidation}
														showErrors={showErrors}
														handleSourceCardClick={this.handleSourceCardClick}
														destinationAccount={destinationAccount}
														templateMode={templateMode}
														biocatchIdentifier={getBiocatchIdentifier}
													/>
												);
											})}
											{paymentIds.length > 1 && (
												<tr className="total-row">
													<td>
														<span className="c-o-list-payment__total-label">Total</span>
													</td>
													<td>
														<span className="c-o-list-payment__total-value">&pound; {total}</span>
													</td>
													<td colSpan="2" />
												</tr>
											)}
										</tbody>
									</table>
									{totalLimitExceeded && <ValidationMessage hasIcon={false} value={content.maxLimitExceededMessage} />}
									{showAddMore && <AddMoreButton title="Add more" onClick={this.handleAddMore} />}
									{hasReachedNumberOfPaymentsLimit && <p id="payment-limit-msg">{content.paymentsLimitMessage}</p>}
								</Row>
								<Row>
									<Column md={12}>
										{showSaveAsTemplate && (
											<SaveAsTemplateToggle
												isChecked={this.state.saveAsTemplate}
												handleToggle={this.handleSaveAsTemplateToggle}
											/>
										)}

										<PageNavigation
											showLoading={hasApiCallInProgress}
											handleContinue={this.handleContinue}
											showContinue={!templateMode}
											enableContinue={!hasApiCallInProgress}
										>
											{templateMode && !showSourcePayeeList && (
												<TemplateButtons
													saveTemplate={() => this.SaveTemplate(handleSaveTemplate)}
													saveTemplateAndExit={() => this.SaveTemplate(handleSaveTemplateAndExit)}
													saveTemplateAndMakePayment={() => this.SaveTemplate(handleSaveTemplateAndMakePayment)}
													isLoading={hasApiCallInProgress}
													saveTemplateButtonText={saveTemplateButtonText}
													saveTemplateAndMakePaymentButtonText={saveTemplateAndMakePaymentButtonText}
													saveTemplateAndExitButtonText={saveTemplateAndExitButtonText}
													showTemplateButtons={showTemplateButtons}
												/>
											)}
											{this.state.saveAsTemplate && (
												<Button
													isLoading={hasApiCallInProgress}
													id="button-save-as-template-exit"
													display="secondary"
													onClick={() => this.SaveTemplate(handleSaveTemplateAndExit)}
													disabled={hasApiCallInProgress}
												>
													{saveTemplateAndExitButtonText}
												</Button>
											)}
										</PageNavigation>
									</Column>
								</Row>
							</Container>
						</div>
					</ServicingSection>
				)}
			</div>
		);
	}
}

DebitPaymentsPage.propTypes = {
	handleContinue: PropTypes.func.isRequired,
	isFormValid: PropTypes.bool.isRequired,
	showAllErrors: PropTypes.func.isRequired,
	hasApiCallInProgress: PropTypes.bool.isRequired,
	content: PropTypes.shape(contentPropTypes).isRequired,
	handleSelectDestinationAccount: PropTypes.func.isRequired,
	addMultiplePayments: PropTypes.func.isRequired,
	handleBacsIdChange: PropTypes.func.isRequired,
	handlePaymentNameChange: PropTypes.func.isRequired,
	destinationAccounts: PropTypes.arrayOf(PropTypes.shape(fromAccountPropType)).isRequired,
	bacsIds: PropTypes.arrayOf(PropTypes.string).isRequired,
	sourcePayees: PropTypes.arrayOf(PropTypes.shape(payeePropTypes)).isRequired,
	hasMultiplePayments: PropTypes.bool.isRequired,
	destinationAccount: PropTypes.string,
	paymentIds: PropTypes.arrayOf(PropTypes.string.isRequired),
	submissionDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(moment)]),
	settlementDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(moment)]),
	handleDateChange: PropTypes.func,
	copyReferenceToAll: PropTypes.func.isRequired,
	hasReachedNumberOfPaymentsLimit: PropTypes.bool.isRequired,
	total: PropTypes.string.isRequired,
	paymentName: PropTypes.string.isRequired,
	handleSelectSourceAccount: PropTypes.func.isRequired,
	updateValidation: PropTypes.func.isRequired,
	validations: PropTypes.shape({}).isRequired,
	hideErrorMsg: PropTypes.func.isRequired,
	handleDisplayFullwidth: PropTypes.func.isRequired,
	bacsId: PropTypes.string.isRequired,
	payments: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			amount: PropTypes.string,
			reference: PropTypes.string,
			transactionCode: PropTypes.string,
		})
	),
	totalLimitExceeded: PropTypes.bool,
	bacsLimit: PropTypes.string,
	sourceAccounts: PropTypes.arrayOf(PropTypes.shape(toAccountPropType)).isRequired,
	paymentsToInternalCreditCards: PropTypes.arrayOf(PropTypes.string),
	handleCancelSourceSelect: PropTypes.func.isRequired,
	handleBack: PropTypes.func.isRequired,
	isAuthorisationEdit: PropTypes.bool.isRequired,
	copyTransactionCodeToAll: PropTypes.func.isRequired,
	templateMode: PropTypes.bool,
	handleSaveTemplate: PropTypes.func,
	handleSaveTemplateAndExit: PropTypes.func,
	handleSaveTemplateAndMakePayment: PropTypes.func,
	templateId: PropTypes.string,
	initValidationForTemplate: PropTypes.func.isRequired,
	handleDeleteTemplate: PropTypes.func,
	removeTemplateNameValidation: PropTypes.func.isRequired,
	isVersionConflict: PropTypes.bool,
	templateLastUpdatedBy: PropTypes.string,
	templateLastUpdatedTime: PropTypes.string,
	addTemplateNameValidation: PropTypes.func.isRequired,
	templateName: PropTypes.string,
	firstPaymentHasFromAccount: PropTypes.bool,
	handleOverwriteTemplate: PropTypes.func,
	goToPayeeSelector: PropTypes.func.isRequired,
	selectPayment: PropTypes.func.isRequired,
	setSelectedDestinationAccount: PropTypes.func.isRequired,
	handleFromCardClick: PropTypes.func.isRequired,
	showFromAccountSelector: PropTypes.bool,
	handleSelectFromAccount: PropTypes.func.isRequired,
	clearSelectedPayment: PropTypes.func.isRequired,
	bacsDebitPaymentId: PropTypes.string.isRequired,
};

export const ConnectWrapper = connect(mapState, mapDispatch)(DebitPaymentsPage);
export default compose(
	connect(mapState, mapDispatch),
	withContent({ content: pageContent }),
	withScrollTop,
	withMissingDetails,
	withAccountSelection
)(DebitPaymentsPage);
